import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FileEdit, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { EditGenericContentForm } from '../schemas/EditGenericContentSchema'
import { useSelectedGenericContentStore } from '../stores/useSelectedGenericContentStore'

type PutEditGenericContentProps = {
  id: string
  tagContent: string
  contentType: string[]
  relativeKeys: string[]
  searchTags: string[]
  fieldKey: string
  fieldValue: string
  infoMessage: string
}

async function putEditGenericContent({
  id,
  ...data
}: PutEditGenericContentProps) {
  return api.backend.put(`/generic-content/${id}`, data)
}

export function useEditGenericContent() {
  const { t } = useTranslation('settings')
  const { genericContent } = useSelectedGenericContentStore()

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PutEditGenericContentProps
  >({
    mutationKey: ['/generic-content/id/edit'],
    mutationFn: putEditGenericContent,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: FileEdit,
          title: t(
            'generic_content.drawer_edit_generic_content.toast_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['generic-content/paginate'],
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t(
            'generic_content.drawer_edit_generic_content.toast_error.title',
          ),
          description:
            error?.response?.data.friendlyMessage ||
            t(
              'generic_content.drawer_edit_generic_content.toast_error.description',
            ),
        },
      })
    },
  })

  const handleEditGenericContent = async (data: EditGenericContentForm) => {
    if (!genericContent) return

    const payload: PutEditGenericContentProps = {
      id: genericContent.id,
      tagContent: data.tagContent,
      contentType: Array.isArray(data.contentType)
        ? data.contentType.map((item) => item.value || item.label || '')
        : [],
      relativeKeys: Array.isArray(data.relativeKeys)
        ? data.relativeKeys.map((item) => item.value || item.label || '')
        : [],
      searchTags: Array.isArray(data.searchTags)
        ? data.searchTags.map((item) => item.value || item.label || '')
        : [],
      fieldKey: data.fieldKey,
      fieldValue: data.fieldValue,
      infoMessage: data.infoMessage || '',
    }

    return mutateAsync(payload)
  }

  return {
    handleEditGenericContent,
    isEditingGenericContent: isPending,
  }
}
